/**
 * My Orders Page - Green & White Theme
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { 
  Link2, 
  ArrowLeft,
  Package,
  Clock,
  CheckCircle,
  Truck,
  XCircle
} from 'lucide-react';

const statusConfig = {
  pending_payment: { icon: Clock, color: 'text-amber-500 bg-amber-50', label: 'Pending Payment' },
  paid: { icon: CheckCircle, color: 'text-blue-500 bg-blue-50', label: 'Paid' },
  fulfilling: { icon: Package, color: 'text-purple-500 bg-purple-50', label: 'Fulfilling' },
  shipped: { icon: Truck, color: 'text-emerald-500 bg-emerald-50', label: 'Shipped' },
  delivered: { icon: CheckCircle, color: 'text-emerald-600 bg-emerald-100', label: 'Delivered' },
  cancelled: { icon: XCircle, color: 'text-red-500 bg-red-50', label: 'Cancelled' }
};

const MyOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders/my');
      setOrders(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-emerald-600 text-white px-6 py-4 sticky top-0 z-50" data-testid="orders-header">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-emerald-700 p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link2 className="h-6 w-6" />
          <span className="font-bold text-lg">My Orders</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-6" data-testid="orders-main">
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-20 w-20 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">No Orders Yet</h2>
            <p className="text-gray-600 mb-6">Start shopping to see your orders here</p>
            <Button 
              onClick={() => navigate('/shop')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-8"
            >
              Start Shopping
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const status = statusConfig[order.status] || statusConfig.pending_payment;
              const StatusIcon = status.icon;
              
              return (
                <div
                  key={order.id}
                  data-testid={`order-${order.id}`}
                  className="bg-white rounded-xl border border-emerald-100 p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs text-gray-500 font-mono">#{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                      <StatusIcon className="h-3 w-3" />
                      {status.label}
                    </span>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-3">
                    <div className="space-y-1">
                      {order.items?.slice(0, 2).map((item, idx) => (
                        <p key={idx} className="text-sm text-gray-600">
                          {item.quantity}x {item.product_title}
                        </p>
                      ))}
                      {order.items?.length > 2 && (
                        <p className="text-sm text-gray-400">
                          +{order.items.length - 2} more items
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <span className="font-bold text-emerald-600">
                        JMD {order.total_amount.toLocaleString()}
                      </span>
                      {order.delivery_package_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/packages/${order.delivery_package_id}`)}
                          className="border-emerald-500 text-emerald-600 text-xs"
                        >
                          Track Delivery
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyOrders;
